import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import RenderPhonePe from '../../components/payment/RenderPhonePe';
import { generateOrderID } from '../../common/CommonFun';
import { postRequest } from '../../apis/ApiInstence';
import { clearCart } from '../../slice/cartSlice';
import statesWithCities from '../../common/data/citydata.json'
import { Link } from 'react-router-dom';

const TOKEN_AMOUNT = 1000;
const shouldForcePhonePeSuccess =
    process.env.NODE_ENV !== "production" &&
    process.env.REACT_APP_PHONEPE_FORCE_SUCCESS === "true";

const Checkout = () => {
    const cartState = useSelector((state) => state.cart);
    const user = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const MySwal = withReactContent(Swal);

    const [paymentMode, setPaymentMode] = useState('');
    const [paymentPlan, setPaymentPlan] = useState('token'); // token | full
    const [paymentDone, setPaymentDone] = useState(false);
    const [guestCheckout, setGuestCheckout] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [profileData, setProfileData] = useState(null);


    // Delivery form fields

    const [flatNo, setFlatNo] = useState('');
    const [guestName, setGuestName] = useState('');

    const [landmark, setLandmark] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [pincode, setPincode] = useState('');
    const [phone, setPhone] = useState('');

    const [searchCity, setSearchCity] = useState("");

    const currentUser = user?.response || (() => {
        try {
            return JSON.parse(localStorage.getItem("user")) || null;
        } catch {
            return null;
        }
    })();

    const isLoggedIn = Boolean(currentUser?.id || currentUser?._id);

    const parseAddress = (address = "") => {
        if (!address) return { flatNo: "", landmark: "", city: "", state: "", pincode: "" };
        const parts = String(address).split(",").map((p) => p.trim()).filter(Boolean);
        let flat = parts[0] || "";
        let cityPart = "";
        let statePart = "";
        let pin = "";

        if (parts.length >= 2) {
            const last = parts[parts.length - 1];
            const pinMatch = last.match(/(\d{5,6})$/);
            if (pinMatch) {
                pin = pinMatch[1];
                statePart = last.replace(pinMatch[1], "").replace(/[-–—]/g, "").trim();
            } else {
                statePart = last;
            }
            cityPart = parts[parts.length - 2] || "";
        }

        const landmarkPart = parts.length > 2 ? parts.slice(1, -2).join(", ") : "";
        return {
            flatNo: flat,
            landmark: landmarkPart,
            city: cityPart,
            state: statePart,
            pincode: pin
        };
    };

    const profileAddress = parseAddress(
        profileData?.address || currentUser?.address || ""
    );
    const profilePhone = profileData?.mobile || currentUser?.mobile || currentUser?.phone || "";

    const [hasPrefilled, setHasPrefilled] = useState(false);
    const getBaseUrl = () => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const backendRoot = process.env.REACT_APP_BACKEND;
        const apiBase = process.env.REACT_APP_API_BASE_URL;
        return (backendUrl || apiBase || backendRoot || "http://localhost:8000")
            .replace(/\/$/, "");
    };

    useEffect(() => {
        if (!isLoggedIn) return;
        const userId = currentUser?._id || currentUser?.id;
        const token = localStorage.getItem("token");
        if (!userId || !token) return;

        const baseUrl = getBaseUrl();
        fetch(`${baseUrl}/customers/one/${userId}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(async (res) => {
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data?.message || "Profile fetch failed");
                return data;
            })
            .then((data) => {
                setProfileData(data);
                localStorage.setItem(
                    "user",
                    JSON.stringify({
                        ...currentUser,
                        name: data?.name || currentUser?.name,
                        mobile: data?.mobile || currentUser?.mobile,
                        address: data?.address || currentUser?.address,
                    })
                );
            })
            .catch(() => {
                // ignore profile fetch errors
            });
    }, [isLoggedIn, currentUser]);

    useEffect(() => {
        const handleUserUpdate = () => {
            try {
                const saved = JSON.parse(localStorage.getItem("user")) || null;
                if (!saved) return;
                if (!hasPrefilled) {
                    setFlatNo(profileAddress.flatNo || saved.address || "");
                    setLandmark(profileAddress.landmark || "");
                    setCity(profileAddress.city || "");
                    setState(profileAddress.state || "");
                    setPincode(profileAddress.pincode || "");
                    setPhone(saved.mobile || saved.phone || "");
                }
            } catch {
                // ignore
            }
        };
        window.addEventListener("userUpdated", handleUserUpdate);
        return () => window.removeEventListener("userUpdated", handleUserUpdate);
    }, [hasPrefilled, profileAddress]);

    useEffect(() => {
        if (!isLoggedIn || hasPrefilled) return;
        setHasPrefilled(true);
        setFlatNo(profileAddress.flatNo || "");
        setLandmark(profileAddress.landmark || "");
        setCity(profileAddress.city || "");
        setState(profileAddress.state || "");
        setPincode(profileAddress.pincode || "");
        setPhone(profilePhone || "");
    }, [isLoggedIn, hasPrefilled, profileAddress, currentUser, profileData]);

    const requiresGuestName = !isLoggedIn && guestCheckout;
    const isFormComplete = Boolean(
        flatNo.trim() &&
        city.trim() &&
        state.trim() &&
        pincode.trim() &&
        phone.trim() &&
        (!requiresGuestName || guestName.trim())
    );

    // Filter cities based on search
    const filteredCities =
        state && statesWithCities[state]
            ? statesWithCities[state].filter((c) =>
                c.toLowerCase().includes(searchCity.toLowerCase())
            )
            : [];

    const cartData = Array.isArray(cartState) && cartState.length > 0
        ? cartState
        : (() => {
            try {
                return JSON.parse(localStorage.getItem("cart")) || [];
            } catch {
                return [];
            }
        })();

    const getItemQty = (item) => item.qty ?? item.quantity ?? 1;
    const getItemPrice = () => TOKEN_AMOUNT;
    const getOriginalPrice = (item) => {
        const price = Number(item.selling_price ?? item.original_price ?? item.price);
        return Number.isFinite(price) ? price : 0;
    };

    const calculateTokenTotal = (items) =>
        items.reduce((total, item) => total + getItemPrice(item) * getItemQty(item), 0);

    const calculateOriginalTotal = (items) =>
        items.reduce((total, item) => total + getOriginalPrice(item) * getItemQty(item), 0);

    const calculateFullTotal = (items) =>
        items.reduce((total, item) => {
            const qty = getItemQty(item);
            const original = getOriginalPrice(item);
            return total + (original + TOKEN_AMOUNT) * qty;
        }, 0);

    const calculateBalanceDue = (items) =>
        calculateOriginalTotal(items);

    const getPayableNow = (items) =>
        paymentPlan === "full" ? calculateFullTotal(items) : calculateTokenTotal(items);

    const getItemPayableNow = (item) => {
        const original = getOriginalPrice(item);
        if (paymentPlan === "full" && original) return original + TOKEN_AMOUNT;
        return getItemPrice(item);
    };

    const handlePlaceOrder = async () => {
        if (!isLoggedIn && !guestCheckout) {
            navigate("/login?redirect=/checkout");
            return;
        }

        // Validation (only required for guest checkout)
        if (!isLoggedIn && guestCheckout) {
            if (!flatNo.trim() || !city.trim() || !state.trim() || !pincode.trim() || !phone.trim()) {
                MySwal.fire({
                    title: 'Missing Fields',
                    text: 'Please fill in all required delivery details.',
                    icon: 'warning',
                });
                return;
            }

            if (!guestName.trim()) {
                MySwal.fire({
                    title: 'Missing Fields',
                    text: 'Please enter your name for guest checkout.',
                    icon: 'warning',
                });
                return;
            }
        }

        if (cartData.length === 0) {
            MySwal.fire({
                title: 'Empty Cart',
                text: 'Please add items to your cart before proceeding.',
                icon: 'warning',
            });
            return;
        }

        const isTokenPlan = paymentPlan === "token";
        const fullTotal = calculateFullTotal(cartData);
        const tokenTotal = calculateTokenTotal(cartData);
        const payableNow = getPayableNow(cartData);
        const balanceDue = isTokenPlan ? calculateOriginalTotal(cartData) : 0;
        const effectivePaymentMethod = isTokenPlan ? "cod" : paymentMode;

        // Build order payload
        const orderDetails = {
            orderID: generateOrderID(),
            customerID: currentUser?._id || currentUser?.id,
            totalAmount: parseFloat(payableNow.toFixed(2)),
            fullAmount: parseFloat(fullTotal.toFixed(2)),
            tokenAmount: parseFloat(tokenTotal.toFixed(2)),
            balanceAmount: parseFloat(balanceDue.toFixed(2)),
            shippingAddress: `${flatNo}, ${landmark ? landmark + ', ' : ''}${city}, ${state} - ${pincode}`,
            orderCustype: guestCheckout ? 'guest' : 'registered',
            flatno: flatNo,
            landmark,
            city,
            state,
            pincode,
            phone,
            guestName: currentUser?.name || guestName,
            paymentMethod: effectivePaymentMethod,
            paymentGateway: paymentMode,
            paymentPlan,
            item_details: cartData.map((item) => ({
                id: item.id || item.product_id,
                product_id: item.id || item.product_id,
                product_name: item.title || item.name,
                price: getOriginalPrice(item) || getItemPrice(item),
                quantity: getItemQty(item),
                total: (getOriginalPrice(item) || getItemPrice(item)) * getItemQty(item),
            })),
            status: 'placed',
            orderDate: new Date().toISOString(),
        };

        try {
            const response = await postRequest('order', orderDetails);

            if (response?.status === 200 || response?.status === 201 || response?.data?.success) {
                dispatch(clearCart());
                localStorage.removeItem('cart');
                localStorage.removeItem('cartItems');
                window.dispatchEvent(new Event("cartUpdated"));
                MySwal.fire({
                    title: '✅ Order Placed!',
                    text: 'Your order was placed successfully.',
                    icon: 'success',
                    confirmButtonText: 'Go to My Orders',
                }).then(() => navigate('/profile?tab=orders'));
            } else {
                throw new Error(response?.data?.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Order Error:', error);
            MySwal.fire({
                title: '❌ Order Failed',
                text: error?.message || 'Something went wrong while placing the order.',
                icon: 'error',
            });
        }
    };

    return (
        <div className="bg-[#fefaf2] min-h-screen pt-[170px] px-4 py-4">
            <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">

                {/* Delivery & Payment Form */}
                <div className="md:col-span-2 bg-white p-6 rounded-2xl shadow-xl">
                    <h2 className="text-2xl font-bold mb-6 text-center text-[#351F72]">
                        🏠 Delivery & Payment
                    </h2><hr />

                    {!isLoggedIn && (
                        <div>
                            <div className="flex items-center justify-center gap-4 my-5">
                                <Link to="/login?redirect=/checkout" className="bg-[#351F72] text-white py-2 px-6 rounded-md">
                                    Login
                                </Link>
                                <div className="text-gray-500 font-semibold">OR</div>
                                <button
                                    className="bg-[#351F72] text-white py-2 px-6 rounded-md"
                                    onClick={() => setGuestCheckout(!guestCheckout)}
                                >
                                    Continue as Guest
                                </button>
                            </div>

                            {!isLoggedIn && guestCheckout && (
                                <form className="space-y-4">
                                    <input
                                        type="text"
                                        value={guestName}
                                        onChange={(e) => setGuestName(e.target.value)}
                                        placeholder="Full Name"
                                        className="w-full border border-gray-400 p-2 rounded-md focus:outline-none focus:ring-1 focus:ring-[#351F72] focus:border-[#351F72]"
                                    />
                                    <input
                                        type="text"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="Mobile Number"
                                        className="w-full border border-gray-400 p-2 rounded-md focus:outline-none focus:ring-1 focus:ring-[#351F72] focus:border-[#351F72]"
                                    />
                                </form>
                            )}
                        </div>
                    )}

                    <form className="space-y-4" >
                        {isLoggedIn && (
                            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                Address details are taken from your profile. To update, edit your profile first.
                                <Link to="/profile" className="ml-2 underline">Go to Profile</Link>
                            </div>
                        )}
                        {isLoggedIn && !currentUser?.address && (
                            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                                No address found in profile. Please fill the missing fields below.
                                <Link to="/profile" className="ml-2 underline">Update Profile</Link>
                            </div>
                        )}
                        {!isLoggedIn && guestCheckout && !isFormComplete && (
                            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                                Please complete all required fields before payment.
                            </div>
                        )}

                        <input
                            type="text"
                            value={flatNo}
                            onChange={(e) => setFlatNo(e.target.value)}
                            placeholder="Flat / House No / Building Name / Street / Area"
                            readOnly={isLoggedIn && Boolean(profileAddress.flatNo)}
                            className={`w-full border border-gray-400 p-2 rounded-md focus:outline-none focus:ring-1 focus:ring-[#351F72] focus:border-[#351F72] ${isLoggedIn && profileAddress.flatNo ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        />
                        <input
                            type="text"
                            value={landmark}
                            onChange={(e) => setLandmark(e.target.value)}
                            placeholder="Landmark (Optional)"
                            readOnly={isLoggedIn && Boolean(profileAddress.landmark)}
                            className={`w-full border border-gray-400 p-2 rounded-md focus:outline-none focus:ring-1 focus:ring-[#351F72] focus:border-[#351F72] ${isLoggedIn && profileAddress.landmark ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                                type="text"
                                value={state}
                                onChange={(e) => {
                                    setState(e.target.value);
                                    setCity("");
                                    setSearchCity("");
                                }}
                                placeholder="State"
                                readOnly={isLoggedIn && Boolean(profileAddress.state)}
                                className={`w-full border border-gray-400 p-2 rounded-md focus:outline-none focus:ring-1 focus:ring-[#351F72] focus:border-[#351F72] ${isLoggedIn && profileAddress.state ? "bg-gray-100 cursor-not-allowed" : ""}`}
                            />

                            <input
                                type="text"
                                value={city}
                                onChange={(e) => {
                                    setCity(e.target.value);
                                    setSearchCity("");
                                    setShowDropdown(false);
                                }}
                                placeholder="City"
                                readOnly={isLoggedIn && Boolean(profileAddress.city)}
                                className={`w-full border border-gray-400 p-2 rounded-md focus:outline-none focus:ring-1 focus:ring-[#351F72] focus:border-[#351F72] ${isLoggedIn && profileAddress.city ? "bg-gray-100 cursor-not-allowed" : ""}`}
                            />
                        </div>
                        <input
                            type="text"
                            value={pincode}
                            onChange={(e) => setPincode(e.target.value)}
                            placeholder="Pincode"
                            readOnly={isLoggedIn && Boolean(profileAddress.pincode)}
                            className={`w-full border border-gray-400 p-2 rounded-md focus:outline-none focus:ring-1 focus:ring-[#351F72] focus:border-[#351F72] ${isLoggedIn && profileAddress.pincode ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        />

                    </form>



                    {
                        (isLoggedIn || guestCheckout) ? <div className="mt-6 mb-6">
                            <h3 className="text-lg font-semibold mb-2">Select Payment Method</h3>
                            <select
                                value={paymentMode}
                                onChange={(e) => {
                                    setPaymentMode(e.target.value);
                                    setPaymentDone(false);
                                }}
                                className="w-full border p-2 rounded-md"
                            >
                                <option value="">-- Select --</option>
                                <option value="phonepe">Pay with PhonePe</option>
                            </select>
                        </div> : ''
                    }


                    {paymentMode === 'phonepe' && !paymentDone && (
                        <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <h4 className="mb-3 text-sm font-semibold text-slate-700">Choose Payment Option</h4>
                            <div className="grid gap-3 md:grid-cols-2">
                                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                                    <input
                                        type="radio"
                                        name="paymentPlan"
                                        value="full"
                                        checked={paymentPlan === "full"}
                                        onChange={() => setPaymentPlan("full")}
                                        className="mt-1"
                                    />
                                    <div>
                                        <p className="text-sm font-semibold text-slate-800">One-time payment</p>
                                        <p className="text-xs text-slate-500">Original price + ₹{TOKEN_AMOUNT} token (paid now)</p>
                                    </div>
                                </label>
                                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                                    <input
                                        type="radio"
                                        name="paymentPlan"
                                        value="token"
                                        checked={paymentPlan === "token"}
                                        onChange={() => setPaymentPlan("token")}
                                        className="mt-1"
                                    />
                                    <div>
                                        <p className="text-sm font-semibold text-slate-800">Token payment</p>
                                        <p className="text-xs text-slate-500">Pay ₹{TOKEN_AMOUNT} now, remaining COD</p>
                                    </div>
                                </label>
                            </div>
                        </div>
                    )}

                    {paymentMode === 'phonepe' && !paymentDone && (
                        <RenderPhonePe
                            currency="INR"
                            amount={Number(getPayableNow(cartData))}
                            userId={currentUser?._id || currentUser?.id || "guest"}
                            customerEmail={currentUser?.email}
                            customerMobile={currentUser?.mobile || phone}
                            disabled={!isLoggedIn && guestCheckout && !isFormComplete}
                            forceSuccess={shouldForcePhonePeSuccess}
                            setPaymentOption={() => {
                                setPaymentDone(true);
                                handlePlaceOrder();
                            }}
                        />
                    )}

                    {paymentMode === "phonepe" && (
                        <p className="mt-4 text-xs leading-6 text-gray-600">
                            By continuing, you agree to our{" "}
                            <Link to="/terms-conditions" className="font-medium text-[#351F72] underline">
                                Terms & Conditions
                            </Link>
                            ,{" "}
                            <Link to="/privacy-policy" className="font-medium text-[#351F72] underline">
                                Privacy Policy
                            </Link>
                            ,{" "}
                            <Link to="/shipping-policy" className="font-medium text-[#351F72] underline">
                                Shipping Policy
                            </Link>
                            ,{" "}
                            <Link to="/return-policy" className="font-medium text-[#351F72] underline">
                                Return Policy
                            </Link>
                            {" "}and{" "}
                            <Link to="/refund-policy" className="font-medium text-[#351F72] underline">
                                Refund & Cancellation Policy
                            </Link>
                            . Online payments are processed through our authorised payment partners, including PhonePe.
                        </p>
                    )}

                    {paymentDone && (
                        <div className="mt-6">
                            <h3 className="text-lg font-semibold text-green-600">✅ Payment Confirmed</h3>
                            <p className="text-sm text-gray-700">Thank you! Your payment is successful.</p>
                        </div>
                    )}
                </div>

                {/* Cart Summary */}
                <div className="bg-white p-6 rounded-2xl shadow-xl">
                    <h3 className="text-lg font-semibold mb-4 text-[#351F72]">🛒 Cart Summary</h3>
                    <div className="space-y-4">
                        {cartData.map((item, index) => {
                            let imagePath = '';
                            if (Array.isArray(item.images) && item.images.length > 0) {
                                imagePath = item.images[0];
                            } else if (typeof item.images === 'string') {
                                try {
                                    const parsedImages = JSON.parse(item.images);
                                    imagePath = Array.isArray(parsedImages) && parsedImages.length > 0 ? parsedImages[0] : '';
                                } catch (error) {
                                    imagePath = item.images?.toString().replace(/[\[\]"]+/g, '').split(',')[0] || '';
                                }
                            }

                            const assetBase = (
                                process.env.REACT_APP_PRODUCT_IMAGE_BASE_URL ||
                                process.env.REACT_APP_ASSET_BASE_URL ||
                                process.env.REACT_APP_BACKEND ||
                                "http://localhost:8000"
                            ).replace(/\/$/, "");

                            const normalizedImagePath = imagePath?.replace?.(/^\/+/, "");
                            const finalUrl = item.item_type === "banner" && item.image
                                ? `${assetBase}/banner/${String(item.image).replace(/^\/+/, "")}`
                                : imagePath
                                    ? (imagePath.startsWith("http")
                                        ? imagePath
                                        : `${assetBase}/uploads/${normalizedImagePath}`)
                                    : "/default-image.png";

                            return (
                                <div key={index} className="flex items-center gap-4">
                                    <img
                                        src={finalUrl}
                                        alt={item.title}
                                        className="w-16 h-16 object-cover rounded-md"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = '/default-image.png';
                                        }}
                                    />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-[#351F72]">{item.title || item.name || "Product"}</p>
                                        <p className="text-sm text-[#351F72]">
                                            ₹{(getItemPayableNow(item) * getItemQty(item)).toFixed(2)} ({getItemQty(item)} × ₹{getItemPayableNow(item)})
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="text-sm text-[#351F72] mt-4">
                        <div>
                            Token Amount: ₹{calculateTokenTotal(cartData).toFixed(2)}
                        </div>
                        <div>
                            Original Price: ₹{calculateOriginalTotal(cartData).toFixed(2)}
                        </div>
                        <div className="font-semibold">
                            Total Price (Original + Token): ₹{(calculateOriginalTotal(cartData) + calculateTokenTotal(cartData)).toFixed(2)}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Checkout;
